import { applyDelta, markVersion } from 'nike-ui/src/common/push/diff';
import { fromJS, Map, OrderedMap, Set } from 'immutable';
import { dispatch, regEventDb } from 'nike-re-framejs';

import { defaultMiddlewares } from '@/common/util';
import { denormalizeSportEventId } from '@/nikeone/domain/sportevent';
import { fetchCombinations, fetchTicket, startTicketSub, stopTicketSub } from './api';
import { scoreboardResetPaths } from '@/scoreboards/services/ticketDetail';
import { betslipOpenEditor } from '@/betslip/handlers';
import PopupNg from '@/ui/PopupNg';
import { fireGAEvent } from '@/analytics';
import { applyCommand } from '@/betslip/commands';
import i18n from '@i18n';
import ticketusClient from '@/_http/axiosTicketusClient';
import { CURRENT_PAGE } from 'nike-ui/src/common/routing';
import { loadCreateAnalysisBetIds } from '@/betbook/handlers';
import { sortBets } from 'nike-ui/src/components/ticketus/handlers';
import { indexById } from 'nike-ui/src/components/turboLive/push/indexingUtils';

regEventDb('ticketus/detail/fetch', defaultMiddlewares, (db, [hashes]) => {
    hashes.map(hash => fetchTicket(hash));
    return db;
});

regEventDb('ticketus/detail/fetched', defaultMiddlewares, (db, [loaded]) => {
    loaded = loaded.update('bets', bets => fromJS(sortBets(bets.toJS())));
    const ticket = loaded.update('flags', Set),
        updatedTicked = markVersion(ticket, loaded.version || 0);
    return db.setIn(['ticketus', 'detail', ticket.get('hash')], updatedTicked);
});

export const ticketusMinimalTicketsFetched = tickets => db => {
    return db.updateIn(['ticketus', 'minimal'], Map(), map => map.concat(indexById(tickets, 'hash')));
};

regEventDb('ticketus/detail/failed', defaultMiddlewares, (db, [hash, error]) => {
    return db.setIn(['ticketus', 'detail', hash], fromJS({ error }));
});

regEventDb('ticketus/detail/subscribe', defaultMiddlewares, (db, [hashes]) => {
    startTicketSub(hashes);
    return db;
});

regEventDb('ticketus/detail/unsubscribe', defaultMiddlewares, db => {
    stopTicketSub();
    return db;
});

regEventDb('ticketus/detail/ticket-snapshot', defaultMiddlewares, (db, [snapshots]) => {
    return db.updateIn(['ticketus', 'detail'], Map(), tickets =>
        snapshots.reduce((acc, snapshot) => {
            snapshot.bets = sortBets(snapshot.bets);
            const ticket = fromJS(snapshot).update('flags', Set);
            const currentCustomerId = db.getIn(['account', 'userData', 'idZak']);
            const ticketCustomerId = ticket.get('customerId');
            const currentUserTicket = ticketCustomerId && currentCustomerId && ticketCustomerId === currentCustomerId;
            const saved = ticket && ticket.get('source') === 'Saved';
            const paidOut = ticket && ticket.get('status') === 'PaidOut';
            if (!paidOut && !saved && currentUserTicket) {
                dispatch(loadCreateAnalysisBetIds(ticket.get('ticketId')));
            }
            return acc.set(ticket.get('hash'), markVersion(ticket, snapshot.version || 0));
        }, tickets)
    );
});

regEventDb('ticketus/detail/ticket-push', defaultMiddlewares, (db, [msg]) => {
    const ticketId = msg.entityId;
    const ticket = db
        .getIn(['ticketus', 'detail'])
        .find(t => t.get('ticketId') === ticketId || t.get('hash') === ticketId);
    if (ticket) {
        const hash = ticket.get('hash');
        return db.updateIn(['ticketus', 'detail', hash], oldTicket => {
            const newTicket = applyDelta(msg.payload, oldTicket, msg.version).toJS();
            newTicket.bets = sortBets(newTicket.bets);
            return fromJS(newTicket);
        });
    }
    return db;
});

regEventDb('ticketus/combinations/fetch', defaultMiddlewares, (db, [hashes]) => {
    hashes.map(hash => fetchCombinations(hash));
    return db;
});

regEventDb('ticketus/combinations/fetched', defaultMiddlewares, (db, [hash, combinations]) => {
    return db.setIn(
        ['ticketus', 'detail', hash, 'combinations'],
        combinations.get('subtickets').reduce((acc, sub) => acc.set(sub.get('combination'), sub), OrderedMap())
    );
});

regEventDb('ticketus/scoreboard/add-match', defaultMiddlewares, (db, [eventId]) => {
    eventId = String(denormalizeSportEventId(eventId));
    const eventIds = [eventId];
    let events = db.getIn(['scoreboard', 'events'], Map());
    events = eventIds.reduce((evts, id) => evts.set(id, db.getIn(['scoreboard', 'events', id])), events);
    scoreboardResetPaths(events.keySeq());
    return db.setIn(['scoreboard', 'events'], events);
});

regEventDb('ticketus/scoreboard/remove-match', defaultMiddlewares, (db, [eventId]) => {
    eventId = String(denormalizeSportEventId(eventId));
    let newDb = db.removeIn(['scoreboard', 'events', eventId]);
    scoreboardResetPaths(newDb.getIn(['scoreboard', 'events']).keySeq());
    return newDb;
});

regEventDb('ticketus/scoreboard/retain-matches', defaultMiddlewares, (db, [eventIds]) => {
    eventIds = eventIds
        .map(denormalizeSportEventId)
        .map(String)
        .toSet();
    const newDb = db.updateIn(['scoreboard', 'events'], (events = Map()) =>
        events.filter((map, eventId) => {
            return eventIds.has(eventId);
        })
    );
    scoreboardResetPaths(newDb.getIn(['scoreboard', 'events']).keySeq());
    return newDb;
});

function creteDraftAndOpenBetslip(createDraftCmd) {
    fireGAEvent(CURRENT_PAGE, 'Novy tiket (Swipe)');
    const cmd = createDraftCmd
        .deleteIn([2, '__meta'])
        .deleteIn([2, 'picks', '__meta'])
        .deleteIn([2, 'stake', '__meta'])
        .deleteIn([2, 'stake', 'stakes', '__meta'])
        .setIn([2, 'retainPicks'], false)
        .toJS();

    dispatch(applyCommand(cmd));
    betslipOpenEditor();
}

function openReplacePicksDialog(bodyText, createDraftCmd) {
    PopupNg.dispatcher.open('dialog/message', {
        bodyText,
        cancelText: i18n('commons.button.zrusit'),
        confirmText: i18n('ticket.replace-ticket'),
        onConfirm: () => creteDraftAndOpenBetslip(createDraftCmd)
    });
}

function addTicketToEditor(db, createDraftCmd, ticketType) {
    PopupNg.dispatcher.close('ticketDetail');
    PopupNg.dispatcher.close('betbook/ticketDetailPopup');

    // na tikete nic nemam
    if (!db.getIn(['server', 'currentPickIds']).size > 0) {
        // typy sa nerovnaju - zapas na rozpisovom sa uz zacal -> dialog ze ho viem preniest len ako jednoduchy
        if (ticketType !== createDraftCmd.getIn([2, 'type'])) {
            openReplacePicksDialog(i18n('ticket.restore.empty.restore-as-standard'), createDraftCmd);
        } else {
            // typy sa rovnaju, a nic nemam na tikete -> prenos bez dialogu
            creteDraftAndOpenBetslip(createDraftCmd);
        }
        // na tikete uz nieco mam -> pri prenose otazka ci nahradit rozpracovany tiket
    } else {
        // typy sa nerovnaju - zapas na rozpisovom sa uz zacal -> dialog ci preniest ako jednoduchy a nahradit rozpracovany
        if (ticketType !== createDraftCmd.getIn([2, 'type'])) {
            openReplacePicksDialog(i18n('ticket.restore.non-empty.restore-as-standard'), createDraftCmd);
            // typy sa rovnaju -> dialog ci nahradit rozpracovany
        } else {
            openReplacePicksDialog(i18n('ticket.restore.non-empty.restore-as-is'), createDraftCmd);
        }
    }
}

export function copyTicketFromDetail(createDraftCmd, ticketType) {
    return db => {
        addTicketToEditor(db, createDraftCmd, ticketType);
        return db;
    };
}

export function copyTicketFromOverview(ticketHash, positionCode, positionId) {
    return db => {
        ticketusClient
            .get(`public-ticket/mobile/${ticketHash}`)
            .then(json => {
                let createDraftCmd = json.createDraftCmd;
                createDraftCmd[2].positionCode = positionCode;
                createDraftCmd[2].positionId = positionId;
                addTicketToEditor(db, fromJS(createDraftCmd), json.type);
            })
            .catch(error => dispatch(['ticketus/detail/failed', ticketHash, error]));
        return db;
    };
}

export const setNewPostTicket = ticketHash => db => db.setIn(['betbook', 'newPost', 'bet', 'ticketHash'], ticketHash);
